import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck, Database, Lock, Cookie, Globe2, UserCheck,
  Mail, Calendar, ArrowUp, ChevronRight, ScrollText,
} from 'lucide-react';

const SUPPORT_EMAIL = 'membership@dominicanchamberfl.org';
const EFFECTIVE_DATE = 'February 1, 2026';

const sections = [
  { id: 'who-we-are', label: '1. Who we are', icon: ShieldCheck },
  { id: 'data-we-collect', label: '2. Data we collect', icon: Database },
  { id: 'how-we-use', label: '3. How we use it', icon: ScrollText },
  { id: 'security', label: '4. Security', icon: Lock },
  { id: 'third-parties', label: '5. Third-party services', icon: Globe2 },
  { id: 'cookies', label: '6. Cookies & tracking', icon: Cookie },
  { id: 'rights', label: '7. Your rights', icon: UserCheck },
  { id: 'children', label: '8. Children', icon: ShieldCheck },
  { id: 'changes', label: '9. Changes to this policy', icon: Calendar },
  { id: 'contact', label: '10. Contact us', icon: Mail },
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy · DCCF Network';
    return () => { document.title = 'DCCF | Dominican Chamber of Commerce of Florida'; };
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-slate-50 pt-14 sm:pt-20" data-testid="privacy-policy-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#002249] text-white">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 10% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #E63946 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002249] via-[#001530] to-[#0a3570]" style={{ mixBlendMode: 'multiply' }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[10.5px] sm:text-xs font-bold text-amber-300 tracking-[0.18em] uppercase mb-3 block">
              Legal · Privacy
            </span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-3">
              Privacy Policy
            </h1>
            <p className="text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">
              How the Dominican Chamber of Commerce of Florida collects, uses, and protects your information.
            </p>
            <p className="text-white/55 text-[13px] mt-4">Effective: {EFFECTIVE_DATE}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">
          {/* Sticky TOC */}
          <aside className="lg:sticky lg:top-24 lg:self-start order-2 lg:order-1" aria-label="Table of contents">
            <nav className="bg-white border border-slate-200 rounded-2xl p-4 lg:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#E63946] mb-3">On this page</p>
              <ul className="space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="group flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] text-slate-600 hover:bg-slate-50 hover:text-[#002249] transition-colors"
                      data-testid={`toc-${s.id}`}
                    >
                      <s.icon className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#E63946] flex-shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Body */}
          <article className="order-1 lg:order-2 max-w-[680px]" data-testid="privacy-content">
            {/* Intro */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <p className="text-[14.5px] sm:text-[15px] text-slate-700 leading-relaxed">
                The <strong className="text-[#002249]">Dominican Chamber of Commerce of Florida</strong> ("DCCF", "we", "our") respects
                your privacy. This Privacy Policy explains what information we collect through our website
                (<a href="https://dominicanchamberfl.org" className="text-[#002249] font-semibold hover:underline">dominicanchamberfl.org</a>),
                our member portal at <span className="font-mono text-[13px] bg-slate-100 px-1.5 py-0.5 rounded">/app</span>,
                and the DCCF Network mobile apps for iOS and Android — and how we use, share, and protect that information.
              </p>
              <p className="text-[14px] text-slate-600 leading-relaxed mt-3">
                By accessing the website, member portal, or apps you agree to the practices described below.
                If you disagree, please discontinue use and contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#002249] font-semibold hover:underline">{SUPPORT_EMAIL}</a>.
              </p>
            </div>

            <Section id="who-we-are" title="1. Who we are" icon={ShieldCheck}>
              <p>
                DCCF is a registered <strong>501(c)(6) nonprofit</strong> organization headquartered in Florida, USA. Our mission is to advocate
                for and connect Dominican-American entrepreneurs, professionals, and businesses across Florida and the Dominican Republic.
              </p>
              <p>
                "DCCF Network" refers to our member-only platform, accessible via the website and the iOS / Android applications.
                The website, portal, and apps share the <strong>same backend, same database, same accounts, and same permissions</strong>.
              </p>
            </Section>

            <Section id="data-we-collect" title="2. Data we collect" icon={Database}>
              <p>We collect only what's needed to power your membership. Specifically:</p>

              <SubHeading>Account &amp; profile data</SubHeading>
              <BulletList items={[
                'Name, email address, and (optional) phone number',
                'Profile photo, bio, leadership title, and language preferences',
                'Business information you choose to publish: company name, industry, website, description, logo',
                'Membership tier and status',
              ]} />

              <SubHeading>Activity &amp; content you create</SubHeading>
              <BulletList items={[
                'Community posts, comments, likes, shares',
                'Direct messages between you and other members',
                'Member deals you publish or save',
                'Event RSVPs and attendance records',
                'Support and contact form submissions',
              ]} />

              <SubHeading>Technical data</SubHeading>
              <BulletList items={[
                'IP address and approximate location (for fraud prevention)',
                'Device type, operating system, browser/app version',
                'Crash logs and basic interaction telemetry to keep the platform running',
              ]} />

              <SubHeading>Data we do NOT collect</SubHeading>
              <BulletList items={[
                'Precise GPS location',
                'Health, financial-account, or biometric data',
                'Browsing history outside DCCF properties',
                'Contact lists from your phone',
              ]} />
            </Section>

            <Section id="how-we-use" title="3. How we use it" icon={ScrollText}>
              <p>We use your data only for legitimate, member-relevant purposes:</p>
              <BulletList items={[
                'Account management — log you in, verify your membership, recover passwords',
                'Platform functionality — render your feed, deliver messages, send event invitations',
                'Communications — operational emails, newsletters you opted into, and chamber announcements',
                'Member directory — show your profile to verified members (you control what is visible)',
                'Safety &amp; abuse prevention — detect spam, harassment, and unauthorized access',
                'Service improvement — diagnose crashes, monitor performance, prioritize features',
                'Legal compliance — respond to lawful requests from regulators or courts',
              ]} />
              <p>
                <strong className="text-[#002249]">We never sell your personal data.</strong> We do not share it with advertisers,
                data brokers, or any third party for marketing purposes.
              </p>
            </Section>

            <Section id="security" title="4. Security" icon={Lock}>
              <p>We protect your data with industry-standard practices:</p>
              <BulletList items={[
                'Transport encryption — TLS 1.2+ on every connection (HTTPS only)',
                'Strict-Transport-Security with preload — your browser refuses to downgrade',
                'Password hashing with bcrypt — we never store passwords in plain text',
                'Role-based access control on the backend — staff only see what their role allows',
                'Encrypted backups stored in a separate region',
                'Routine security audits and dependency updates',
              ]} />
              <p>
                Despite these measures, no system is 100 % secure. If we ever discover a breach affecting
                your data, we will notify you within <strong>72 hours</strong> by email and post a public
                advisory at <a href="https://dominicanchamberfl.org" className="text-[#002249] font-semibold hover:underline">dominicanchamberfl.org</a>.
              </p>
            </Section>

            <Section id="third-parties" title="5. Third-party services" icon={Globe2}>
              <p>
                We use carefully selected third-party services to run the platform. Each operates under
                contractual data-protection terms and only receives the minimum data they need.
              </p>
              <ServiceList items={[
                { name: 'Resend', purpose: 'Transactional and newsletter email delivery (operational only)', region: 'United States' },
                { name: 'Anthropic Claude', purpose: 'AI writing assistance for the admin email composer (no member data is sent — only the admin\'s prompt)', region: 'United States' },
                { name: 'Cloudflare', purpose: 'Content delivery, DDoS protection, edge caching of public pages', region: 'Global' },
                { name: 'MongoDB Atlas', purpose: 'Primary database (encrypted at rest)', region: 'United States' },
                { name: 'Apple Push Notification service', purpose: 'Push notifications to iOS devices (when enabled)', region: 'United States' },
                { name: 'Google Firebase Cloud Messaging', purpose: 'Push notifications to Android devices (when enabled)', region: 'United States' },
              ]} />
              <p className="text-[13px] text-slate-500 mt-2">
                We do <strong>not</strong> use third-party advertising networks, retargeting pixels, or social-media tracking
                scripts on the member portal or in the apps.
              </p>
            </Section>

            <Section id="cookies" title="6. Cookies &amp; tracking" icon={Cookie}>
              <p>
                We use a small number of <strong>strictly necessary</strong> first-party cookies to keep you logged in
                and to remember your interface preferences. We also use a Cloudflare bot-management cookie
                (<span className="font-mono text-[13px] bg-slate-100 px-1.5 py-0.5 rounded">__cf_bm</span>) to block automated abuse.
              </p>
              <p>
                We do <strong>not</strong> use third-party advertising or analytics cookies. You can clear or block all
                DCCF cookies in your browser settings; doing so may sign you out and disable some features.
              </p>
            </Section>

            <Section id="rights" title="7. Your rights" icon={UserCheck}>
              <p>You have full control over your data:</p>
              <BulletList items={[
                'Access — request a copy of the data we hold about you',
                'Correction — update inaccurate or incomplete data directly in your profile',
                'Deletion — request permanent deletion of your account and associated content',
                'Portability — request a machine-readable export of your data',
                'Restriction — ask us to limit how your data is used',
                'Objection — object to specific processing activities',
                'Withdraw consent — opt out of newsletters at any time',
              ]} />
              <p>
                Most actions are available directly inside the platform — go to{' '}
                <strong className="text-[#002249]">Profile → Settings</strong>. For everything else, email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#002249] font-semibold hover:underline">{SUPPORT_EMAIL}</a>{' '}
                with the subject line "Privacy request" and we will respond within <strong>30 days</strong>.
              </p>
              <p>
                If you are based in the European Union, United Kingdom, California, or another jurisdiction
                with specific privacy rights (GDPR, UK GDPR, CCPA/CPRA, LGPD, etc.), those rights are honored
                here in addition to the rights above.
              </p>
            </Section>

            <Section id="children" title="8. Children" icon={ShieldCheck}>
              <p>
                DCCF Network is intended for adults 18 years and older. We do not knowingly collect personal
                information from anyone under 18. If you believe a minor has created an account, please email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#002249] font-semibold hover:underline">{SUPPORT_EMAIL}</a>{' '}
                and we will remove the account promptly.
              </p>
            </Section>

            <Section id="changes" title="9. Changes to this policy" icon={Calendar}>
              <p>
                We may update this policy as our services evolve. When we make material changes, we will:
              </p>
              <BulletList items={[
                'Post the updated policy at /privacy with a new "Effective" date',
                'Notify members by email at least 14 days before changes take effect',
                'Highlight the changes inside the platform on next sign-in',
              ]} />
              <p>
                Continued use of DCCF Network after the effective date constitutes acceptance of the updated policy.
              </p>
            </Section>

            <Section id="contact" title="10. Contact us" icon={Mail}>
              <p>
                Questions, requests, or concerns about this policy or your data?
              </p>
              <div className="bg-gradient-to-br from-[#002249] to-[#001530] rounded-2xl p-6 sm:p-7 text-white mt-3 not-prose">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber-300 mb-2">Privacy contact</p>
                <p className="text-[16px] font-bold tracking-tight">Membership Relations</p>
                <p className="text-[13px] text-white/70 mt-1.5 leading-relaxed">Dominican Chamber of Commerce of Florida</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#E63946] hover:bg-[#c02a35] text-white text-[13px] font-bold transition-colors"
                  data-testid="privacy-contact-cta"
                >
                  <Mail className="w-4 h-4" />
                  {SUPPORT_EMAIL}
                </a>
                <p className="text-[12px] text-white/60 mt-3">We respond to privacy requests within 30 days.</p>
              </div>
            </Section>

            {/* Footer nav */}
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <Link to="/" className="text-[13px] font-semibold text-[#002249] hover:underline inline-flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back to DCCF
              </Link>
              <button
                onClick={scrollTop}
                className="text-[12.5px] font-semibold text-slate-500 hover:text-[#002249] inline-flex items-center gap-1"
                data-testid="privacy-scroll-top"
              >
                <ArrowUp className="w-3.5 h-3.5" /> Back to top
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-components ──────────────────────────────────────────────────────── */

const Section = ({ id, title, icon: Icon, children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.08 }}
    transition={{ duration: 0.45 }}
    className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] scroll-mt-24"
    data-testid={`section-${id}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="w-9 h-9 rounded-xl bg-[#002249]/5 text-[#002249] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <h2 className="text-[19px] sm:text-[22px] font-bold text-[#002249] tracking-tight leading-tight">{title}</h2>
    </div>
    <div className="prose-content space-y-3 text-[14px] sm:text-[14.5px] text-slate-700 leading-relaxed">
      {children}
    </div>
  </motion.section>
);

const SubHeading = ({ children }) => (
  <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#E63946] mt-5 mb-2">
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 my-2 ml-0.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-[14px] text-slate-600 leading-relaxed">
        <span className="w-1.5 h-1.5 rounded-full bg-[#E63946] mt-2 flex-shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: item }} />
      </li>
    ))}
  </ul>
);

const ServiceList = ({ items }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-200 my-3 not-prose">
    {items.map((s) => (
      <div key={s.name} className="p-4">
        <p className="text-[13.5px] font-bold text-[#002249]">{s.name} <span className="text-[10.5px] font-normal text-slate-400 uppercase tracking-wider ml-1">· {s.region}</span></p>
        <p className="text-[12.5px] text-slate-600 mt-0.5 leading-relaxed">{s.purpose}</p>
      </div>
    ))}
  </div>
);

export default PrivacyPolicyPage;
